import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileStack, CalendarDays, Clock, AlertOctagon, Scale, Stamp, Files, ChevronRight } from "lucide-react";
import { TaskList } from "@/components/TaskList";
import { TaskFormDialog } from "@/components/TaskFormDialog";
import { CompleteDialog } from "@/components/CompleteDialog";
import { SyncBar } from "@/components/SyncBar";
import { useQuickCreate } from "@/context/QuickCreate";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const MetricCard = ({ label, value, icon: Icon, to, testId, dark }) => (
  <Link
    to={to}
    data-testid={testId}
    className={cn(
      "flex flex-col justify-between rounded-xl border p-5 transition-shadow duration-150 hover:shadow-md",
      dark
        ? "border-navy bg-navy text-white"
        : "border-border bg-card text-card-foreground shadow-shb"
    )}
  >
    <div className="flex items-start justify-between gap-3">
      <span
        className={cn(
          "text-[12px] font-semibold uppercase tracking-[0.6px]",
          dark ? "text-white/70" : "text-muted-foreground"
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
          dark ? "bg-white/10 text-white" : "bg-muted text-muted-foreground"
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
    </div>
    <span className="mt-6 font-mono text-3xl font-semibold tabular-nums">{value}</span>
  </Link>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [completing, setCompleting] = useState(null);
  const { refreshKey } = useQuickCreate();

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/dashboard");
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [refreshKey]);

  useEffect(() => { load(); }, [load]);

  const c = data?.counts || {};
  const newDocs = Object.values(data?.new_documents || {}).reduce((a, b) => a + b, 0);
  const today = (data?.recent || []).filter((t) => t.bucket === "today" || t.bucket === "overdue");
  const upcoming = (data?.recent || []).filter((t) => t.bucket === "upcoming" || t.bucket === "later");

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">Merhaba, Umut Sağlam</p>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-foreground">Kontrol Paneli</h1>
        </div>
        <SyncBar label="Sheet Eşitle" />
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard dark label="Yeni Evraklar" value={newDocs} icon={FileStack}
          to="/isler?record_type=Gelen%20Evrak&status=Yapılacak&section=all" testId="metric-new-docs" />
        <MetricCard label="Bugünkü İşler" value={c.today ?? 0} icon={CalendarDays} to="/isler?range=today" testId="metric-today" />
        <MetricCard label="Yaklaşan İşler" value={c.upcoming ?? 0} icon={Clock} to="/isler?range=upcoming" testId="metric-upcoming" />
        <MetricCard label="Geciken İşler" value={c.overdue ?? 0} icon={AlertOctagon} to="/isler?range=overdue" testId="metric-overdue" />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {[
          { name: "Adliye", to: "/adliye", icon: Scale },
          { name: "TÜRKPATENT", to: "/turkpatent", icon: Stamp },
          { name: "Diğer", to: "/diger", icon: Files },
        ].map(({ name, to, icon: Icon }) => {
          const s = data?.sections?.[name] || {};
          return (
            <Link
              key={name}
              to={to}
              data-testid={`section-card-${name}`}
              className="flex items-center gap-4 rounded-xl border border-border bg-card p-5 shadow-shb transition-shadow duration-150 hover:shadow-md"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground">
                <Icon className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="font-display text-lg font-bold tracking-tight text-foreground">{name}</p>
                <p className="text-xs text-muted-foreground">
                  {s.open ?? 0} açık iş · {s.overdue ?? 0} geciken · {data?.new_documents?.[name] ?? 0} yeni evrak
                </p>
              </div>
              <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <section className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h2 className="font-display text-xl font-bold tracking-tight">Bugünkü İşler</h2>
            <Link to="/isler?range=today" className="text-xs font-semibold text-crimson hover:underline" data-testid="today-link">
              Tümü
            </Link>
          </div>
          <TaskList
            tasks={today}
            loading={loading}
            compact
            onEdit={(t) => { setEditing(t); setFormOpen(true); }}
            onComplete={setCompleting}
            onRefresh={load}
            emptyText="Bugün için iş yok."
          />
        </section>

        <section className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h2 className="font-display text-xl font-bold tracking-tight">Yaklaşan İşler</h2>
            <Link to="/isler?range=upcoming" className="text-xs font-semibold text-crimson hover:underline" data-testid="upcoming-link">
              Tümü
            </Link>
          </div>
          <TaskList
            tasks={upcoming}
            loading={loading}
            compact
            onEdit={(t) => { setEditing(t); setFormOpen(true); }}
            onComplete={setCompleting}
            onRefresh={load}
            emptyText="Yaklaşan iş yok."
          />
        </section>
      </div>

      <TaskFormDialog
        open={formOpen}
        onOpenChange={(v) => { setFormOpen(v); if (!v) setEditing(null); }}
        initial={editing}
        onSaved={load}
      />
      <CompleteDialog task={completing} open={!!completing} onOpenChange={(v) => !v && setCompleting(null)} onDone={load} />
    </div>
  );
}
